import React, { useState, useEffect } from 'react';

// React DateTime
import moment from 'moment';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';
import SearchAndFilter from '../../../components/Search/SearchAndFilter';
import TeasuryTable from '../../../components/Content/TeasuryTable';
import Pagination from '../../../components/Footer/Pagination';

// Utils
import { parseCurrency, getAlert } from '../../../../utils/SamuiUtils';
import { formatDate } from '../../../../utils/DateUtils';
import {
  useDataTableKeyboardNavigation,
  getDataTableRowStyle,
  getDataTableKeyboardInstructions
} from '../../../../utils/DataTableKeyboardNavigation';

const Main = ({ name, onChangeMode, onPageUpdate, dataMasterList, dataDetailList }) => {
  const [filteredDetailList, setFilteredDetailList] = useState([]);
  const [wtDocNo, setWtDocNo] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    localStorage.getItem('itemsPerPageDropdown') || 10
  );

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState(
    localStorage.getItem('searchTerm') || ''
  );
  const [filteredItems, setFilteredItems] = useState([]);

  // Date filter states
  const [startDate, setStartDate] = useState(() => {
    const storedStartDate = localStorage.getItem('startDate');
    return storedStartDate || moment().startOf('month').format('YYYY-MM-DD');
  });

  const [endDate, setEndDate] = useState(() => {
    const storedEndDate = localStorage.getItem('endDate');
    return storedEndDate || moment().endOf('month').format('YYYY-MM-DD');
  });

  // Initialize data
  useEffect(() => {
    if (dataMasterList && dataMasterList.length > 0) {
      setCurrentPage(1);
      filterItems();
    } else {
      setFilteredItems([]);
    }
  }, [dataMasterList]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedRowIndex(0);
    filterItems();
  }, [searchTerm, startDate, endDate, dataMasterList]);

  // Filter items function
  const filterItems = () => {
    try {
      let filtered = [...dataMasterList];

      const start = moment(startDate, 'YYYY-MM-DD');
      const end = moment(endDate, 'YYYY-MM-DD');

      filtered = filtered.filter((item) => {
        const itemDate = moment(item.WTDoc_Date, 'YYYY-MM-DD');
        return itemDate.isBetween(start, end, null, '[]');
      });

      // Search filtering
      if (searchTerm.trim() !== '') {
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        filtered = filtered.filter((item) =>
          (item.WTDoc_No && item.WTDoc_No.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.WTDoc_Emp_Name && item.WTDoc_Emp_Name.toLowerCase().includes(lowercasedSearchTerm)) ||
          (item.WTDocType_Name && item.WTDocType_Name.toLowerCase().includes(lowercasedSearchTerm))
        );
      }

      setFilteredItems(filtered);
    } catch (error) {
      getAlert('FAILED', error.message);
      setFilteredItems([]);
    }
  };

  // Pagination calculations
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedRowIndex(0);
    localStorage.setItem('currentPage', pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  const onRowSelected = (fromData, index = -1) => {
    setSelectedRowIndex(index);
    setSelectedRow(fromData.WTDoc_Id);
    const filteredDetail = dataDetailList.filter(data => parseCurrency(data.WTDoc_Id) === fromData.WTDoc_Id);

    if (filteredDetail && filteredDetail.length > 0) {
      setFilteredDetailList(filteredDetail);
    } else {
      setFilteredDetailList([]);
    }

    setWtDocNo(fromData.WTDoc_No);
  };

  // Field mappings for keyboard navigation
  const fieldMappings = {
    id: 'WTDoc_Id'
  };

  // Keyboard navigation
  useDataTableKeyboardNavigation(
    true, // isActive
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    onRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems.length,
    itemsPerPage
  );

  return <>
    <Breadcrumbs page={name} items={[
      { name: 'ใบยืม-ใบเบิก', url: '/borrow-withdrawal' },
      { name: name, url: '/technical-equip-form' },
    ]} />
    <div className="container-fluid">
      {/* Search and Filter Section */}
      <SearchAndFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searchPlaceholder="ค้นหาเลขเอกสาร, ชื่อลูกค้า, ประเภทเอกสาร..."
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        showDateFilter={true}
        onAddNew={onChangeMode("I")}
        addButtonText={`สร้าง${name}`}
        showAddButton={true}
      />

      <div className="card py-3">
        <div className="row ">
          <div className="col-4 mb-1" style={{ borderRight: '2px solid #c7c8c9' }}>
            <div className="table-responsive" style={{ maxHeight: '650px', overflowY: 'auto' }}>
              <table id="basic-datatables" className="table table-striped table-hover">
                <thead className="thead-dark">
                  <tr>
                    <th className="text-center" style={{ width: '10%' }}>ลำดับ</th>
                    <th className="text-center" style={{ width: '25%' }}>วันที่เอกสาร</th>
                    <th className="text-center" style={{ width: '30%' }}>เลขเอกสาร</th>
                    <th className="text-center" style={{ width: '30%' }}>ชื่อลูกค้า</th>
                    <th className="text-center" style={{ width: '5%' }}>แก้ไข</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((data, index) => (
                      <tr
                        key={data.WTDoc_Id || index + 1}
                        onClick={() => {
                          setSelectedRowIndex(index);
                          onRowSelected(data, index);
                        }}
                        style={getDataTableRowStyle(
                          index,
                          selectedRowIndex,
                          selectedRow,
                          data,
                          'WTDoc_Id'
                        )}>
                        <td className="text-center">{indexOfFirstItem + index + 1}</td>
                        <td className="text-center">{formatDate(data.WTDoc_Date)}</td>
                        <td className="text-center">{data.WTDoc_No}</td>
                        <td className="text-center">{data.WTDoc_Emp_Name}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm"
                            style={{ color: '#EF6C00', background: 'transparent', border: 'none', padding: '4px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onPageUpdate && data.WTDoc_No) {
                                onPageUpdate(data.WTDoc_No);
                              }
                            }}
                            title="แก้ไข"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" style={{ color: '#EF6C00' }}>
                              <path d="M12.146 0.146a.5.5 0 01.708 0l3 3a.5.5 0 010 .708l-10 10a.5.5 0 01-.233.131l-5 1a.5.5 0 01-.608-.608l1-5a.5.5 0 01.131-.233l10-10zM11.207 2L3 10.207V12h1.793L14 3.793 11.207 2z" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination & Items per page selector */}
            <div className="bg-white mt-2" style={{ padding: '8px 0' }}>
              <div className="row m-0">
                <div className="col-12 d-flex justify-content-between align-items-center flex-wrap py-2">
                  <div className="d-flex align-items-center flex-wrap">
                    <span className="me-2">แสดง:</span>
                    <select
                      className="form-select me-2"
                      style={{ width: 'auto', minWidth: '70px' }}
                      value={itemsPerPage}
                      onChange={(e) => {
                        const newItemsPerPage = parseInt(e.target.value);
                        setItemsPerPage(newItemsPerPage);
                        localStorage.setItem('itemsPerPageDropdown', newItemsPerPage);
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>รายการต่อหน้า | แสดง {indexOfFirstItem + 1} ถึง {Math.min(indexOfLastItem, filteredItems.length)} จาก {filteredItems.length} รายการ</span>
                  </div>
                  <div className="d-flex align-items-center flex-wrap">
                    <div className="text-muted me-3" style={{ fontSize: "12px" }}>
                      {getDataTableKeyboardInstructions(false, true)}
                    </div>
                    <Pagination
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      itemsPerPage={itemsPerPage}
                      dataMasterList={filteredItems}
                      handlePageChange={handlePageChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-8 mb-3">
            {filteredDetailList.length !== 0 && (
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="card-title mb-0">
                  เอกสารเลขที่: {wtDocNo}
                </p>
              </div>
            )}
            <TeasuryTable dataList={filteredDetailList} />
          </div>
        </div>

        {/* Keyboard Instructions */}
        {getDataTableKeyboardInstructions()}
      </div>
    </div>
  </>;
};

export default Main;
